import { z } from "zod";
import type { ActivityKind } from "./activity.js";
import type { Currency } from "./money.js";

/**
 * Il testo di una notifica push.
 *
 * ── Perché sta qui e non nel server ───────────────────────────────────────
 *
 * Perché è **la parte che si sbaglia**, ed è l'unica verificabile senza un
 * browser, senza un dispositivo e senza un servizio di terze parti. Il resto
 * del push — cifratura, endpoint, permessi — o funziona o dà errore; il testo
 * invece può funzionare benissimo e dire la cosa sbagliata.
 *
 * ── Cosa può dire ─────────────────────────────────────────────────────────
 *
 * Le stesse etichette del registro attività: **copie del momento**, non lo
 * stato di adesso. Una notifica arriva subito dopo il fatto, quindi la
 * differenza è quasi sempre nulla — ma «quasi» non è «mai», e rileggere la
 * spesa per costruire il testo significherebbe che una notifica in ritardo
 * racconta una storia diversa da quella avvenuta.
 */
export interface NotificationText {
  title: string;
  body: string;
}

/** Quello che serve per scrivere il testo: sono le colonne dell'evento. */
export interface NotifiableEvent {
  kind: ActivityKind;
  groupName: string;
  actorName: string;
  subjectLabel: string | null;
  counterpartLabel: string | null;
  amountCents: number | null;
  currency: Currency | null;
}

function money(cents: number, currency: Currency): string {
  const amount = (cents / 100).toFixed(2).replace(".", ",");

  return currency === "EUR" ? `${amount} €` : `${amount} ${currency}`;
}

/** L'importo se c'è, altrimenti stringa vuota da concatenare senza `if`. */
function withAmount(text: string, event: NotifiableEvent): string {
  if (event.amountCents === null || event.currency === null) return text;

  return `${text} · ${money(event.amountCents, event.currency)}`;
}

/**
 * Il testo per un fatto, oppure `null` se quel fatto **non merita una vibrazione**.
 *
 * `group_created` non la merita: un gruppo nasce con dentro solo chi l'ha
 * creato, quindi non esiste nessun altro a cui dirlo. Suonerebbe come una
 * notifica mandata a sé stessi.
 *
 * Il criterio non è «è importante?» ma «c'è qualcuno che lo vuole sapere
 * *adesso*?». Il registro attività racconta tutto lo stesso, senza svegliare
 * nessuno.
 */
export function notificationFor(event: NotifiableEvent): NotificationText | null {
  const subject = event.subjectLabel ?? "qualcosa";

  switch (event.kind) {
    case "expense_created":
      return {
        title: `Nuova spesa in ${event.groupName}`,
        body: withAmount(`${event.actorName} ha aggiunto ${subject}`, event),
      };

    case "expense_updated":
      return {
        title: `Spesa modificata in ${event.groupName}`,
        // «Adesso» perché l'importo dell'evento è quello *dopo* la modifica,
        // mentre il nome è quello di prima: la stessa asimmetria del foglio
        // dei dettagli, detta invece che lasciata intuire.
        body: withAmount(`${event.actorName} ha modificato ${subject} · adesso`, event),
      };

    case "expense_deleted":
      return {
        title: `Spesa eliminata in ${event.groupName}`,
        body: withAmount(`${event.actorName} ha eliminato ${subject}`, event),
      };

    case "settlement_created":
      return {
        title: `Pagamento in ${event.groupName}`,
        body: withAmount(`${subject} → ${event.counterpartLabel ?? "qualcuno"}`, event),
      };

    case "settlement_deleted":
      return {
        title: `Pagamento annullato in ${event.groupName}`,
        body: withAmount(
          `${event.actorName} ha annullato ${subject} → ${event.counterpartLabel ?? "qualcuno"}`,
          event,
        ),
      };

    case "member_added":
      return {
        title: `Nuova persona in ${event.groupName}`,
        body: `${event.actorName} ha aggiunto ${subject}`,
      };

    case "member_joined":
      return {
        title: `${subject} è entrato in ${event.groupName}`,
        body: "Adesso partecipa alle spese del gruppo.",
      };

    case "member_removed":
      return {
        title: `Una persona in meno in ${event.groupName}`,
        body: `${event.actorName} ha tolto ${subject} dal gruppo`,
      };

    case "friend_added":
      return {
        title: "Nuova amicizia",
        body: `${event.actorName} ti ha aggiunto agli amici`,
      };

    case "group_created":
      return null;
  }
}

/**
 * Il carico che viaggia dentro il push, e che il service worker legge.
 *
 * Va tenuto **piccolo**: il messaggio cifrato ha un tetto di circa 4 KB, e
 * superarlo non dà un errore chiaro — dà una notifica che non arriva. Qui non
 * ci sta niente che non serva a mostrarla o ad aprire il posto giusto.
 */
export const pushPayloadSchema = z.object({
  title: z.string().min(1),
  body: z.string().min(1),
  /** Dove portare chi tocca la notifica. Percorso, mai URL assoluto. */
  path: z.string().startsWith("/"),
  /**
   * Il raggruppatore: due notifiche con lo stesso `tag` si sostituiscono
   * invece di accumularsi. Per gruppo, così dieci spese di fila lasciano una
   * riga sola sulla schermata di blocco.
   */
  tag: z.string().min(1),
});

export type PushPayload = z.infer<typeof pushPayloadSchema>;

/** Un dispositivo iscritto: è la forma che restituisce `PushManager.subscribe`. */
export const pushSubscriptionSchema = z.object({
  endpoint: z.string().url().max(2000),
  keys: z.object({
    p256dh: z.string().min(1).max(255),
    auth: z.string().min(1).max(255),
  }),
});

export type PushSubscriptionInput = z.infer<typeof pushSubscriptionSchema>;

export const pushPublicKeyResponseSchema = z.object({
  /** Vuota quando il server non ha le chiavi VAPID: il client si spegne da solo. */
  publicKey: z.string(),
});
